/**
 * Explore Data - Phase D3.2
 * Experience pins for the Explore view with real GPS coordinates
 * 3 curated local experiences around the hotel in Lisbon
 * Enhanced with concierge-style descriptions and detailed info
 */

export interface ExplorePin {
  id: string;
  name: string;
  type: 'museum' | 'beach' | 'restaurant';
  subtitle: string;
  distance: string;
  description: string;
  // Concierge-style rich description for detail view
  conciergeDescription: string;
  image: string;
  // Gallery images for detail panel
  images: string[];
  // Real GPS coordinates
  coordinates: { lat: number; lng: number };
  // Icon for the pin
  icon: string;
  // Phase D3.2: Enhanced info
  travelTime: string;
  openingHours: string;
  priceRange?: string;
  hotelBadge: 'recommended' | 'partner';
  // Voice response snippets
  voiceResponses: {
    tellMeMore: string;
    whatIsThis: string;
    isFar: string;
    howToGetThere: string;
    book: string;
  };
}

/**
 * Hotel location - Chiado, Lisbon
 */
export const LISBON_MAP = {
  center: 'Baixa-Chiado, Lisbon',
  hotelName: 'The Lumière Hotel',
  // Hotel at Chiado: 38.7103°N, 9.1422°W
  coordinates: { lat: 38.7103, lng: -9.1422 },
};

/**
 * 3 Experience Pins - Real Lisbon locations with GPS coordinates
 * Enhanced with concierge-style content for Phase D3.2
 */
export const EXPLORE_PINS: ExplorePin[] = [
  {
    id: 'pin_museum',
    name: 'MAAT Museum',
    type: 'museum',
    subtitle: 'Museum of Art, Architecture and Technology',
    distance: '4.2 km',
    description: 'Contemporary museum on the Tagus riverfront with striking architecture and rotating exhibitions.',
    conciergeDescription: `The MAAT is one of Lisbon's most striking cultural landmarks. Its undulating rooftop offers panoramic views of the Tagus River and the 25 de Abril Bridge. Inside, you'll find thought-provoking exhibitions that blend art, architecture, and technology in ways that feel both intimate and grand. The building itself, designed by Amanda Levete, is a work of art — many guests spend as much time admiring the architecture as the exhibitions inside.`,
    image: '/images/explore/museum.jpg',
    images: [
      // Real MAAT museum photos — Wikimedia Commons, CC BY-SA 4.0 / CC BY 4.0
      'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Panorama_view_of_the_outside_of_MAAT_museum_in_Lisbon.jpg/800px-Panorama_view_of_the_outside_of_MAAT_museum_in_Lisbon.jpg',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/View_from_the_MAAT_Rooftop_Toward_Ponte_25_de_Abril_and_the_Lisbon_Waterfront_%2854702003134%29.jpg/960px-View_from_the_MAAT_Rooftop_Toward_Ponte_25_de_Abril_and_the_Lisbon_Waterfront_%2854702003134%29.jpg',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/MAAT_Museum_of_Art_Architecture_and_Technology_Lisbon.jpg/960px-MAAT_Museum_of_Art_Architecture_and_Technology_Lisbon.jpg',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a3/MAAT_Museum_Lisbon.jpg/960px-MAAT_Museum_Lisbon.jpg',
    ],
    coordinates: { lat: 38.6966, lng: -9.1956 },
    icon: '🏛️',
    travelTime: '15 min by taxi',
    openingHours: '10:00 – 19:00',
    priceRange: '€9 entry',
    hotelBadge: 'recommended',
    voiceResponses: {
      tellMeMore: "The MAAT is a stunning museum right on the Tagus riverfront. Its architecture alone is worth the visit — you can walk on the rooftop for incredible views. Inside, the exhibitions explore the intersection of art and technology. It's one of my favourite places to recommend.",
      whatIsThis: "This is the MAAT — the Museum of Art, Architecture and Technology. It's a contemporary museum in Belém, known for its futuristic design and rotating exhibitions.",
      isFar: "It's about 4 kilometres from the hotel, roughly a 15-minute taxi ride. I'd recommend going in the morning to avoid crowds.",
      howToGetThere: "The easiest way is by taxi — about 15 minutes from here. You could also take tram 15E from Praça da Figueira, which takes around 25 minutes and offers lovely views along the river.",
      book: "The MAAT doesn't require advance booking for general admission. However, I can arrange a private guided tour if you'd like a more curated experience. Just let me know.",
    },
  },
  {
    id: 'pin_beach',
    name: 'Praia de Carcavelos',
    type: 'beach',
    subtitle: 'Popular golden sand beach',
    distance: '15 km',
    description: "One of Lisbon's most popular beaches with golden sand, great waves for surfing, and beachfront cafés.",
    conciergeDescription: `Carcavelos is the beach the locals love — and for good reason. The sand is golden, the waves are perfect for surfing or just playing in the water, and there's a relaxed energy that makes you forget you're only 20 minutes from the city. The beachfront bars serve fresh grilled fish and cold drinks, and if you time it right, the sunset here is simply unforgettable. It's the perfect escape when you want to feel the ocean breeze.`,
    image: '/images/explore/beach.jpg',
    images: [
      // Carcavelos beach february 2025 — Panek, CC BY-SA 4.0, via Wikimedia Commons
      'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1e/Carcavelos_beach_february_2025.jpg/960px-Carcavelos_beach_february_2025.jpg',
      // Sunset on Praia de Carcavelos (50662756717) — Sonse, CC BY 2.0, via Wikimedia Commons
      'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2e/Sunset_on_Praia_de_Carcavelos_%E2%80%A2_Carcavelos_beach_%2850662756717%29.jpg/960px-Sunset_on_Praia_de_Carcavelos_%E2%80%A2_Carcavelos_beach_%2850662756717%29.jpg',
      // Sunset on Praia de Carcavelos (50661934563) — Sonse, CC BY 2.0, via Wikimedia Commons
      'https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Sunset_on_Praia_de_Carcavelos_%E2%80%A2_Carcavelos_beach_%2850661934563%29.jpg/500px-Sunset_on_Praia_de_Carcavelos_%E2%80%A2_Carcavelos_beach_%2850661934563%29.jpg',
      // Puddle in the beach of Moinho, Carcavelos — Ivanhercaz, CC BY-SA 4.0, via Wikimedia Commons
      'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Puddle_in_the_beach_of_Moinho%2C_Carcavelos.jpg/960px-Puddle_in_the_beach_of_Moinho%2C_Carcavelos.jpg',
    ],
    coordinates: { lat: 38.6780, lng: -9.3310 },
    icon: '🏖️',
    travelTime: '25 min by train',
    openingHours: 'Open all day',
    priceRange: undefined,
    hotelBadge: 'recommended',
    voiceResponses: {
      tellMeMore: "Carcavelos is a beautiful golden sand beach, very popular with locals. The waves are great for surfing, but also gentle enough to swim. There are lovely beachfront cafés where you can have fresh seafood and watch the sunset. It's the perfect day trip.",
      whatIsThis: "This is Praia de Carcavelos — one of the best beaches near Lisbon. Golden sand, good waves, and a very relaxed atmosphere. Locals love it.",
      isFar: "It's about 15 kilometres from the hotel — roughly 25 minutes by train from Cais do Sodré station. The train ride along the coast is quite scenic.",
      howToGetThere: "The best way is by train from Cais do Sodré station. It's a pleasant 25-minute ride along the coast. Trains run every 20 minutes or so. I can also arrange a car if you prefer.",
      book: "No booking needed — it's a public beach. But if you'd like, I can reserve sun loungers at one of the beach clubs, or arrange a surfing lesson. Just say the word.",
    },
  },
  {
    id: 'pin_restaurant',
    name: 'Belcanto',
    type: 'restaurant',
    subtitle: 'Two Michelin Star fine dining',
    distance: '200 m',
    description: "Chef José Avillez's flagship restaurant offering innovative Portuguese cuisine in an intimate setting.",
    conciergeDescription: `Belcanto is Chef José Avillez's crown jewel — a two Michelin star restaurant that reimagines Portuguese cuisine with creativity and soul. The tasting menu is a journey through flavours you won't forget: from the iconic "Garden of the Goose that Laid the Golden Eggs" to dishes that pay homage to traditional recipes while surprising you at every turn. The dining room is intimate and elegant, the service impeccable. This is where you come to celebrate something special — or to make an ordinary evening extraordinary.`,
    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop',
    images: [
      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=800&h=600&fit=crop',
    ],
    coordinates: { lat: 38.7102, lng: -9.1418 },
    icon: '🍽️',
    travelTime: '3 min walk',
    openingHours: '19:00 – 23:00',
    priceRange: '€185 tasting menu',
    hotelBadge: 'partner',
    voiceResponses: {
      tellMeMore: "Belcanto is Chef José Avillez's two Michelin star restaurant, just steps from the hotel. The tasting menu is extraordinary — inventive Portuguese cuisine that surprises and delights. The atmosphere is intimate, the service flawless. It's perfect for a special evening.",
      whatIsThis: "This is Belcanto — a two Michelin star restaurant by Chef José Avillez. It's considered one of the finest dining experiences in Portugal, known for creative takes on traditional dishes.",
      isFar: "Not at all — it's just 200 metres from the hotel. A three-minute walk. You could be there in no time.",
      howToGetThere: "It's literally around the corner — a 3-minute walk from the hotel. Turn left out of the lobby, and you'll see it on Largo de São Carlos. I'd be happy to walk you there personally.",
      book: "Belcanto requires reservations well in advance, especially for dinner. As a hotel partner, we have priority access. Shall I check availability for you? Just tell me your preferred date and time.",
    },
  },
];

/**
 * Get pin by type for voice commands
 */
export function findPinByType(type: ExplorePin['type']): ExplorePin | undefined {
  return EXPLORE_PINS.find(pin => pin.type === type);
}

/**
 * Get pin by id
 */
export function findPinById(id: string): ExplorePin | undefined {
  return EXPLORE_PINS.find(pin => pin.id === id);
}

/**
 * Voice command matching for explore pins
 */
export function parseExploreVoice(transcript: string): ExplorePin['type'] | null {
  const lower = transcript.toLowerCase();

  if (/\b(museums?|maat|art\s*museum)\b/i.test(lower)) {
    return 'museum';
  }

  if (/\b(beach|beaches|praia|carcavelos|seaside|ocean)\b/i.test(lower)) {
    return 'beach';
  }

  if (/\b(restaurants?|belcanto|dining|eat|food)\b/i.test(lower)) {
    return 'restaurant';
  }

  return null;
}
